import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { subscriptionKeys } from '@/store/subscriptions/subscriptions.keys';

import {
  addContacts,
  assignSeat,
  changePreferences,
  deleteContacts,
  getAllPeople,
  getMe,
  getPeople,
  getPeopleByIds,
  getPersonById,
  onboardPerson,
  removeSeat,
  setActivationStatus,
  updateAvatar,
  updateContact,
  updatePerson,
} from './people.api';
import { peopleKeys } from './people.keys';
import type {
  ActivationStatus,
  AssignSeatInput,
  GetAllPeopleParams,
  GetPeopleParams,
  NewContact,
  PersonPreferences,
  RemoveSeatInput,
  UpdateContactInput,
  UpdatePersonInput,
} from './people.types';

/**
 * People do change — a seat moves, a name or avatar is edited — but every one of those
 * paths runs through a mutation here, and each invalidates what it touched. Invalidation
 * refetches irrespective of staleness, so the only fetches this suppresses are the ones
 * nothing asked for: a remount, a tab regaining focus.
 *
 * Not `Infinity` like `me` and the price catalogue: a person can also be edited from
 * outside this tab — another session, the main Haven app — and nothing tells this one. A
 * few minutes bounds how long a stale name or avatar can survive here.
 */
const PEOPLE_STALE_TIME = 5 * 60 * 1000;

export const usePeopleQuery = (params: GetPeopleParams) =>
  useQuery({
    queryKey: peopleKeys.list(params),
    queryFn: () => getPeople(params),
    enabled: !!params.serviceId,
    staleTime: PEOPLE_STALE_TIME,
  });

export const useAllPeopleQuery = (params: GetAllPeopleParams = {}) =>
  useQuery({
    queryKey: peopleKeys.allList(params),
    queryFn: () => getAllPeople(params),
    staleTime: PEOPLE_STALE_TIME,
  });

export const usePersonQuery = (id?: string) =>
  useQuery({
    queryKey: peopleKeys.detail(id!),
    queryFn: () => getPersonById(id!),
    enabled: !!id,
    staleTime: PEOPLE_STALE_TIME,
  });

/**
 * The signed-in person. Held for the whole session: who the caller is cannot change under
 * it — a different token means a fresh load — and the handful of mutations that edit their
 * own row invalidate `peopleKeys.me()`, which refetches regardless of staleness.
 *
 * Worth pinning because this one gates the rest: the vessels query stays disabled until it
 * yields an `ownerId`, so refetching it on every mount or window focus stalls the page
 * behind a request whose answer is already known.
 */
export const useMeQuery = () =>
  useQuery({
    queryKey: peopleKeys.me(),
    queryFn: getMe,
    staleTime: Infinity,
  });

export const usePeopleByIdsQuery = (ids: string[]) =>
  useQuery({
    queryKey: peopleKeys.byIds(ids),
    queryFn: () => getPeopleByIds(ids),
    enabled: ids.length > 0,
    staleTime: PEOPLE_STALE_TIME,
  });

export const useUpdatePersonMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePersonInput }) =>
      updatePerson(id, input),
    onSuccess: (_person, { id }) => {
      queryClient.invalidateQueries({ queryKey: peopleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: peopleKeys.me() });
    },
  });
};

export const useSetActivationStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ActivationStatus }) =>
      setActivationStatus(id, status),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: peopleKeys.detail(id) });
    },
  });
};

export const useOnboardPersonMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => onboardPerson(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: peopleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: peopleKeys.me() });
    },
  });
};

export const useUpdateAvatarMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => updateAvatar(id, file),
    onSuccess: (_file, { id }) => {
      queryClient.invalidateQueries({ queryKey: peopleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: peopleKeys.me() });
    },
  });
};

export const useAddContactsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contacts }: { id: string; contacts: NewContact[] }) =>
      addContacts(id, contacts),
    onSuccess: (_contacts, { id }) => {
      queryClient.invalidateQueries({ queryKey: peopleKeys.detail(id) });
    },
  });
};

export const useUpdateContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateContactInput }) =>
      updateContact(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: peopleKeys.detail(id) });
    },
  });
};

export const useDeleteContactsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contactIds }: { id: string; contactIds: string[] }) =>
      deleteContacts(id, contactIds),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: peopleKeys.detail(id) });
    },
  });
};

/**
 * What a seat change reaches: people, because the seat lives on a person's role row, and
 * subscriptions, since a seat's availability is read off the subscription's counts.
 */
const invalidateSeats = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: peopleKeys.all });
  queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
};

/**
 * Assigns a subscription seat, moving the person out of their previous one if they held one.
 * Refetched on a refusal too: that usually means the view is stale — the pool filled up, or
 * the plan changed underneath.
 */
export const useAssignSeatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignSeatInput) => assignSeat(input),
    onSettled: () => invalidateSeats(queryClient),
  });
};

/** Empties a seat, freeing it for someone else. The person keeps their role on the vessel. */
export const useRemoveSeatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RemoveSeatInput) => removeSeat(input),
    onSuccess: () => invalidateSeats(queryClient),
  });
};

export const useChangePreferencesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, preferences }: { id: string; preferences: PersonPreferences }) =>
      changePreferences(id, preferences),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: peopleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: peopleKeys.me() });
    },
  });
};
